import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { User, Trophy, Swords } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PageTitle } from '../../components/layout/PageTitle';
import { useI18n } from '../../contexts/I18nContext';

interface PlayerStats {
  username: string;
  avatar_url?: string;
  victories: number;
  defeats: number;
  total_matches: number;
  win_rate: number;
  user_id?: string; // Optionnel pour éviter l'erreur
}

export function Ranking() {
  const [players, setPlayers] = useState<PlayerStats[]>([]);
  const [loading, setLoading] = useState(true);
  const { t } = useI18n();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data, error } = await supabase
          .from('player_stats')
          .select('username, avatar_url, victories, defeats, total_matches, win_rate')
          .order('rank', { ascending: true })
          .order('victories', { ascending: false });

        if (error) {
          console.error('Error fetching rankings:', error);
        } else {
          setPlayers(data || []);
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        <span className="sr-only">{t('pvp_ranking.loading')}</span>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
      <Helmet>
        <title>{t('pvp_ranking.seo.title')}</title>
        <meta name="description" content={t('pvp_ranking.seo.description')} />
      </Helmet>
      <PageTitle title={t('pvp_ranking.title')} />
      <div className="text-center mb-4 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 sm:mb-4">{t('pvp_ranking.title')}</h1>
        <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto px-2">
          {t('pvp_ranking.subtitle')}
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="bg-primary-600 text-white p-4 sm:p-6">
          <div className="flex items-center justify-center space-x-2">
            <Trophy className="w-5 h-5 sm:w-6 sm:h-6" />
            <h2 className="text-xl sm:text-2xl font-bold">{t('pvp_ranking.scoreboard')}</h2>
          </div>
        </div>

        <div className="p-3 sm:p-6">
          <div className="bg-gray-50 p-3 sm:p-4 rounded-lg mb-4 sm:mb-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4">
              <div className="bg-white p-3 sm:p-4 rounded-lg shadow">
                <div className="text-xl sm:text-2xl font-bold text-primary-600">{players.length}</div>
                <div className="text-xs sm:text-sm text-gray-600">{t('pvp_ranking.stats.ranked_players')}</div>
              </div>
              <div className="bg-white p-3 sm:p-4 rounded-lg shadow">
                <div className="text-xl sm:text-2xl font-bold text-primary-600">
                  {players.reduce((acc, player) => acc + player.total_matches, 0)}
                </div>
                <div className="text-xs sm:text-sm text-gray-600">{t('pvp_ranking.stats.matches_played')}</div>
              </div>
              <div className="col-span-2 sm:col-span-1 bg-white p-3 sm:p-4 rounded-lg shadow flex items-center space-x-3">
                <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full overflow-hidden bg-gray-200">
                  {players[0]?.avatar_url ? (
                    <img
                      src={players[0]?.avatar_url}
                      alt={`Avatar de ${players[0]?.username}`}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <User className="h-full w-full p-2 text-gray-400" />
                  )}
                </div>
                <div>
                  <div className="text-xs sm:text-sm text-gray-600">{t('pvp_ranking.stats.best_player')}</div>
                  <div className="font-bold text-primary-600 text-sm sm:text-base">{players[0]?.username || '-'}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Top 3 */}
          <div className="mb-6 sm:mb-8 grid grid-cols-3 gap-2 sm:gap-4">
            {/* 2ème place */}
            <div className="col-span-1">
              {players[1] && (
                <div className="text-center">
                  <div className="relative inline-block">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto rounded-full overflow-hidden border-4 border-gray-300 shadow-lg">
                      {players[1].avatar_url ? (
                        <img src={players[1].avatar_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-full h-full p-3 sm:p-4 text-gray-400" />
                      )}
                    </div>
                    <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-gray-300 text-white w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-bold text-sm sm:text-lg">2</div>
                  </div>
                  <div className="mt-4 font-semibold text-gray-800 text-sm sm:text-base truncate px-1">{players[1].username}</div>
                  <div className="text-xs sm:text-sm text-gray-600">{players[1].win_rate}% {t('pvp_ranking.stats.win_rate')}</div>
                </div>
              )}
            </div>

            {/* 1ère place */}
            <div className="col-span-1 -mt-2 sm:-mt-4">
              {players[0] && (
                <div className="text-center">
                  <div className="relative inline-block">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto rounded-full overflow-hidden border-4 border-yellow-400 shadow-lg">
                      {players[0].avatar_url ? (
                        <img src={players[0].avatar_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-full h-full p-3 sm:p-4 text-gray-400" />
                      )}
                    </div>
                    <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-yellow-400 text-white w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-bold text-sm sm:text-lg">1</div>
                  </div>
                  <div className="mt-4 font-bold text-gray-900 text-sm sm:text-base truncate px-1">{players[0].username}</div>
                  <div className="text-xs sm:text-sm text-gray-600">{players[0].win_rate}% {t('pvp_ranking.stats.win_rate')}</div>
                </div>
              )}
            </div>

            {/* 3ème place */}
            <div className="col-span-1">
              {players[2] && (
                <div className="text-center">
                  <div className="relative inline-block">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto rounded-full overflow-hidden border-4 border-amber-700 shadow-lg">
                      {players[2].avatar_url ? (
                        <img src={players[2].avatar_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-full h-full p-3 sm:p-4 text-gray-400" />
                      )}
                    </div>
                    <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-amber-700 text-white w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-bold text-sm sm:text-lg">3</div>
                  </div>
                  <div className="mt-4 font-semibold text-gray-800 text-sm sm:text-base truncate px-1">{players[2].username}</div>
                  <div className="text-xs sm:text-sm text-gray-600">{players[2].win_rate}% {t('pvp_ranking.stats.win_rate')}</div>
                </div>
              )}
            </div>
          </div>

          {/* Tableau des joueurs */}
          <div className="overflow-x-auto -mx-3 sm:mx-0">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('pvp_ranking.table.rank')}</th>
                  <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('pvp_ranking.table.player')}</th>
                  <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('pvp_ranking.table.wins_losses')}</th>
                  <th scope="col" className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('pvp_ranking.table.total')}</th>
                  <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('pvp_ranking.table.win_percentage')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {players.map((player, index) => (
                  <tr 
                    key={player.username} 
                    className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} 
                      ${index < 3 ? 'bg-opacity-50 font-medium' : ''}`}
                  >
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500">
                      {index + 1}
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0">
                          {player.avatar_url ? (
                            <img className="h-8 w-8 sm:h-10 sm:w-10 rounded-full" src={player.avatar_url} alt="" />
                          ) : (
                            <User className="h-8 w-8 sm:h-10 sm:w-10 text-gray-400" />
                          )}
                        </div>
                        <div className="ml-2 sm:ml-4">
                          <div className="text-xs sm:text-sm font-medium text-gray-900">
                            {player.username}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                      {player.victories}/{player.defeats}
                    </td>
                    <td className="hidden sm:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {player.total_matches}
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                      <div className="text-xs sm:text-sm text-gray-900">{player.win_rate}%</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
